import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import * as Bixby from '$/core';
import { AppState } from '$/store';
import InputBlock from '$/components/global/inputBlock';
import { updateDraft } from '$/store/draft/actions';
import Loading from '$/components/loading/Loading';
import { GridFactory } from '$/factory/GridFactory';
import { Formik, Field, FormikProps } from 'formik';
import BixCheckbox from '$/components/global/bixCheckbox';
import { AgGridReact } from 'ag-grid-react';
import { ColumnApi, GridApi } from 'ag-grid-enterprise';
import './CompanyTransactionDetails.scss';

export interface ICompanyTransactionDetailsProps {
  draftCompany?: Bixby.Models.DraftCompany;
  currentCompany?: Bixby.Models.AdminCompany;
  isEditing?: boolean;
  isLoading: boolean;
  discardDraft?: boolean;
  dispatchUpdateDraftCompany: (companyId: string, updatedCompany: Bixby.Models.JsonPatchOperation[]) => void;
}

export interface CompanyTransactionDetailsValues {
  isVisible: boolean;
  notes: string;
}

const CompanyTransactionDetails: React.FC<ICompanyTransactionDetailsProps> = props => {
  const { draftCompany, currentCompany, isEditing, discardDraft, dispatchUpdateDraftCompany, isLoading } = props;
  const [initialFormValues, setInitialFormValues] = useState({} as CompanyTransactionDetailsValues);
  const [transactionDetailRows, setTransactionDetailRows] = useState(
    undefined as Bixby.Models.AdminTransactionDetailGridRow[] | undefined
  );
  const [gridApi, setGridApi] = useState(null as GridApi | null);
  const [, setGridColumnApi] = useState(null as ColumnApi | null);

  useEffect(() => {
    if (isEditing && draftCompany && draftCompany.transactionDetails) {
      setTransactionDetailRows(GridFactory.buildAdminTransactionDetails(draftCompany.transactionDetails));
      setInitialFormValues({
        isVisible: draftCompany.transactionDetails.isVisible,
        notes: draftCompany.transactionDetails.notes || '',
      });
    } else if ((!isEditing || discardDraft) && currentCompany && currentCompany.transactionDetails) {
      setTransactionDetailRows(GridFactory.buildAdminTransactionDetails(currentCompany.transactionDetails));
      setInitialFormValues({
        isVisible: currentCompany.transactionDetails.isVisible,
        notes: currentCompany.transactionDetails.notes || '',
      });
    }
  }, [isEditing, discardDraft, draftCompany, currentCompany]);

  const formikRef = useRef<FormikProps<CompanyTransactionDetailsValues>>(null);

  useEffect(() => {
    if (discardDraft) {
      handleResetForm();
    }
  }, [discardDraft]);

  const handleResetForm = () => {
    if (formikRef && formikRef.current) {
      formikRef.current.resetForm();
    }
  };

  const handleCellValueChange = () => {
    const rowData: Bixby.Models.AdminTransactionDetailGridRow[] = [];
    if (gridApi) {
      gridApi.forEachNode(node => rowData.push(node.data));
    }
    setTransactionDetailRows(rowData);
  };

  const onGridReady = (params: any) => {
    setGridApi(params.api);
    setGridColumnApi(params.columnApi);
  };

  const getContextMenuItems = () => {
    const result = ['copy', 'copyWithHeaders', 'separator', 'export'];
    return result;
  };

  return isLoading || !transactionDetailRows ? (
    <Loading inline />
  ) : (
    <div className="admin-profile-section bottom">
      <React.Fragment>
        <section>
          <h3>Transaction Details</h3>
          {isEditing && (
            <div className="transaction_details_data_grid">
              <div className="grid_container">
                <div className="grid ag-theme-alpine">
                  <AgGridReact
                    enableRangeSelection={true}
                    getContextMenuItems={getContextMenuItems}
                    defaultColDef={{
                      flex: 1,
                      minWidth: 100,
                      sortable: true,
                      resizable: true,
                      lockPinned: true,
                      cellClass: 'align-left',
                    }}
                    onGridReady={onGridReady}
                    rowData={transactionDetailRows}
                    suppressDragLeaveHidesColumns={true}
                    suppressMovableColumns={true}
                    animateRows={true}
                    rowHeight={32}
                    headerHeight={32}
                    onCellValueChanged={handleCellValueChange}
                    suppressLastEmptyLineOnPaste={true}
                    columnDefs={[
                      {
                        headerName: 'Original Buyout Details',
                        field: 'rowTitle',
                        cellClass: 'align-left',
                        editable: false,
                        minWidth: 250,
                      },
                      {
                        headerName: '',
                        field: 'name',
                        cellClass: 'align-left',
                        editable: true,
                        minWidth: 250,
                      },
                      {
                        headerName: '',
                        field: 'value',
                        cellClass: 'align-left',
                        editable: true,
                        minWidth: 250,
                      },
                    ]}></AgGridReact>
                </div>
              </div>
            </div>
          )}
          {!isEditing && (
            <div className="transaction_details_data_grid">
              <div className="grid_container">
                <div className="grid ag-theme-alpine">
                  <AgGridReact
                    enableRangeSelection={false}
                    getContextMenuItems={getContextMenuItems}
                    defaultColDef={{
                      flex: 1,
                      minWidth: 100,
                      sortable: true,
                      resizable: true,
                      lockPinned: true,
                      cellClass: 'align-left',
                    }}
                    onGridReady={onGridReady}
                    rowData={transactionDetailRows}
                    suppressDragLeaveHidesColumns={true}
                    suppressMovableColumns={true}
                    animateRows={true}
                    rowHeight={32}
                    headerHeight={32}
                    columnDefs={[
                      {
                        headerName: 'Original Buyout Details',
                        field: 'rowTitle',
                        cellClass: 'align-left',
                        minWidth: 250,
                      },
                      {
                        headerName: '',
                        field: 'name',
                        cellClass: 'align-left',
                        minWidth: 250,
                      },
                      {
                        headerName: '',
                        field: 'value',
                        cellClass: 'align-left',
                        minWidth: 250,
                      },
                    ]}></AgGridReact>
                </div>
              </div>
            </div>
          )}
          <Formik
            innerRef={formikRef}
            initialValues={initialFormValues}
            enableReinitialize
            onSubmit={(values: CompanyTransactionDetailsValues) => {
              if (draftCompany && transactionDetailRows) {
                const parsed = GridFactory.convertTableToCompanyTransactionDetails(
                  transactionDetailRows,
                  values.isVisible,
                  values.notes
                );
                const updateValues: Bixby.Models.JsonPatchOperation[] = [
                  {
                    op: 'replace',
                    path: `/transactionDetails`,
                    value: parsed,
                    from: '',
                  },
                ];

                dispatchUpdateDraftCompany(draftCompany.id, updateValues);
              }
            }}
            render={({ handleSubmit }) => (
              <form className="transaction_details_form" onSubmit={handleSubmit}>
                <div className="profile-input-container">
                  <div className="profile-input-column">
                    <Field
                      name="isVisible"
                      render={({ field }: { [key: string]: any }) => (
                        <BixCheckbox
                          id="isVisible"
                          checked={field.value}
                          disabled={!isEditing}
                          title="Indicates whether transaction details appears on the company overview."
                          onChange={field.onChange(field.name)}>
                          Is Visible
                        </BixCheckbox>
                      )}
                    />
                    <InputBlock title="Notes">
                      <Field name="notes" component="textarea" disabled={!isEditing} />
                    </InputBlock>
                  </div>
                </div>
                {isEditing && (
                  <button className="bix_button" type="submit">
                    Save as Draft
                  </button>
                )}
              </form>
            )}></Formik>
        </section>
      </React.Fragment>
    </div>
  );
};

const mapStateToProps = (state: AppState) => {
  return {
    isLoading: state.draftState.isLoading,
    error: state.draftState.error,
    draftCompany: state.draftState.draftCompany,
    isEditing: state.draftState.isEditing,
    discardDraft: state.draftState.discardDraft,
    currentCompany: state.companyState.currentCompany,
  };
};

const mapDispatchToProps = (dispatch: any) => ({
  dispatchUpdateDraftCompany: (companyId: string, draftCompanyCommand: Bixby.Models.JsonPatchOperation[]) =>
    dispatch(updateDraft(companyId, draftCompanyCommand)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CompanyTransactionDetails);
